<template>
  <search-page
    :page-size="page ? page.size : undefined"
    :page-size-options="pageSizeOptions"
    :results-wrapper-class="resultsWrapperClass"
    :results-subheader="
      page && page.numberOfElements
        ? $t('common.resultsSubheader', {
            firstIndex: page.number * page.size + 1,
            lastIndex: page.number * page.size + page.numberOfElements,
            totalElements: page.totalElements
          })
        : undefined
    "
    @selectPageSize="selectPageSize($event)"
  >
    <!--
    ============================================================================
    Dynamic components
    ============================================================================
    -->
    <template v-for="c in dynamicComponents">
      <component
        :ref="c.key"
        :key="c.key"
        :is="c.component"
        v-bind="c.props"
        v-on="c.events"
      />
    </template>

    <!--
    ============================================================================
    Debug
    ============================================================================
    -->
    <debug-panel slot="debug">{{ { parameters, page } }}</debug-panel>

    <!--
    ============================================================================
    Parameters
    ============================================================================
    -->
    <search-parameters-editor
      slot="parameters"
      v-if="parameters"
      v-model="parameters"
      @submit="find()"
    />

    <!--
    ============================================================================
    Search buttons
    ============================================================================
    -->
    <template slot="search-buttons">
      <b-button variant="cancel" class="mr-auto" @click.prevent="hideModal()">
        {{ $t("common.cancel") }}
      </b-button>
      <b-button variant="find" @click.prevent="find()">
        {{ $t("common.find") }}
      </b-button>
    </template>

    <!--
    ============================================================================
    Alerts
    ============================================================================
    -->
    <template v-if="page && page.onlineResult" slot="alerts">
      <common-error-alerts :status-code="page.onlineResult.statusCode" />
    </template>

    <!--
    ============================================================================
    Results (slot)
    ============================================================================
    -->
    <template slot="results">
      <!--
      ==========================================================================
      Remove sheet
      ==========================================================================
      -->
      <modal-sheet ref="removeSheet" @hidden="removeSheetHidden">
        <b-button
          block
          variant="danger"
          @click.prevent="confirmRemove(itemToRemove)"
        >
          {{ $t("common.remove") }}
        </b-button>
        <b-button
          block
          variant="cancel"
          @click.prevent="$refs.removeSheet.hide()"
        >
          {{ $t("common.cancel") }}
        </b-button>
      </modal-sheet>

      <template v-for="(c, i) in beforeTableComponents()">
        <component :key="i" :is="c.component" v-bind="c.props" />
      </template>

      <!--
      ==========================================================================
      Results (table)
      ==========================================================================
      -->
      <data-table
        :class="dataTableClass"
        striped
        hover
        condensed
        :stacked="isSmDown"
        :value="page && page.content"
        :sortedBy="page && page.sortedBy"
        :sortOrder="page && page.sortOrder"
        :columns="columns"
        @sort="sortBy"
      >
        <!--
        <template v-slot:header(companyName)="{ col, c }">
          <div class="col" :key="c">
            {{ col.key }}
          </div>
        </template>
        -->

        <!--
        <template v-slot:cell(companyName)="{ row, col, r, c }">
          <div class="col" :key="c">
            <router-link :to="{ name: 'Provider', params: { id: row.id } }">
              {{ row.companyName }}
            </router-link>
          </div>
        </template>
        -->

        <template v-slot:cell(lastModified)="{ row, col, r, c }">
          <div
            class="col"
            :key="c"
            :class="{
              'text-success border-success':
                row.lastModified &&
                row.lastModifiedOffline &&
                row.lastModified > row.lastModifiedOffline,
              'text-danger border-danger':
                row.lastModified &&
                row.lastModifiedOffline &&
                row.lastModified < row.lastModifiedOffline
            }"
          >
            {{ $formatDateTime(row.lastModified) }}
          </div>
        </template>

        <template v-slot:cell(lastModifiedOffline)="{ row, col, r, c }">
          <div
            class="col"
            :key="c"
            :class="{
              'text-success border-success':
                row.lastModified &&
                row.lastModifiedOffline &&
                row.lastModified < row.lastModifiedOffline,
              'text-danger border-danger':
                row.lastModified &&
                row.lastModifiedOffline &&
                row.lastModified > row.lastModifiedOffline
            }"
          >
            {{ $formatDateTime(row.lastModifiedOffline) }}
          </div>
        </template>

        <!--
        <template v-slot:footer(companyName)="{ col, c }">
          <div class="col" :key="c">
            {{ col.key }}
          </div>
        </template>
        -->
      </data-table>
    </template>

    <!--
    ============================================================================
    Buttons
    ============================================================================
    -->
    <template slot="buttons">
      <pager
        class="float-left"
        :page="page"
        @selectPageSize="selectPageSize($event)"
        @selectPage="selectPage($event)"
      />

      <!--<b-button variant="cancel" class="float-left">Cancel</b-button>-->
      <b-button
        variant="add"
        v-if="page && page.permissions.create"
        @click.prevent="onAdd"
      >
        {{ $t("common.add") }}
      </b-button>
    </template>
  </search-page>
</template>
<script>
import SearchPageMixin from "vue-template/protected/mixins/SearchPageMixin";
import lowerFirst from "lodash/lowerFirst";
import Vue from "vue";

export default {
  mixins: [SearchPageMixin],

  data() {
    return {
      dynamicComponents: []
    };
  },

  props: {
    columns: {
      required: true
    },

    resultsWrapperClass: {},

    dataTableClass: {
      default: "h-100 mh-100"
    }
  },

  methods: {
    addComponent(c, { props, events } = {}) {
      var ComponentClass = Vue.extend(c);
      var instance = new ComponentClass({
        propsData: props,
        i18n: this._i18n
      });

      //var instance = this.$createElement(c);

      //instance.$slots.default = [ 'Click me!' ]
      instance.$mount(); // pass nothing
      if (events) {
        for (let k of Object.keys(events)) {
          instance.$on(k, events[k]);
        }
      }
      this.$refs.container.appendChild(instance.$el);
      instance.$on("hook:beforeDestroy", () => {
        //console.log('Removing child');
        this.$refs.container.removeChild(instance.$el);
      });
      return instance;
    },

    beforeTableComponents() {
      return [];
    },

    breadcrumbs() {
      const routeName = this.$route.name;

      return [
        {
          icon: "home",
          text: "Home",
          to: "/protected"
        },
        {
          text: () => this.$t("menu." + lowerFirst(routeName))
        }
      ];
    },

    /*
    $formatDateTime(dt) {
      const format = LoggedUserService.getDateTimeFormat();
      return this.$moment2text(this.$text2moment(dt), format + " HH:mm:ss");
    },
    */

    onAdd() {
      alert("Virtual method");
    },

    remove(result) {
      this.itemToRemove = result;
      this.$refs.removeSheet.show();
    },

    removeSheetHidden() {
      this.itemToRemove = undefined;
    },

    async confirmRemove(result) {
      try {
        await this.getService().remove({
          id: result.id
        });
      } catch (e) {
        console.error("remove-error", e);
        return this.handleError(e);
      }

      await this.find();
      this.$refs.removeSheet.hide();
    }
  }
};
</script>
